'use client';

/**
 * LoginTransition.tsx — innloggingsanimasjon
 *
 * Fullskjerms-overlay over login-siden i samme stil som AI-chatten og headeren:
 * blå linje som tegnes opp (fremdrift), lyssveip langs linjen, pustende aura,
 * og glød-blink når det er ferdig. Selve tidsstyringen (5 s + 1 s pause) eies
 * av LoginSection; denne komponenten er ren presentasjon styrt av `phase`.
 *
 * - running: linjen tegnes opp over `durationMs`, statusteksten bytter underveis
 * - done:    linjen er full, glød-blink, «Velkommen» (pausen før redirect)
 * - leaving: dashbordet er lastet under overlayet; det tones rolig ut (LoginLeaving → onLeft)
 * - failed:  overlayet trekkes raskt tilbake (LoginSection fjerner det etterpå)
 */

import { useEffect, useState } from 'react';
import styles from './login-transition.module.css';

export type LoginPhase = 'running' | 'done' | 'failed' | 'leaving';

const STEPS = ['Verifiserer identitet', 'Henter tilganger', 'Forbereder arbeidsflate'];
/** Tid per statustekst — 3 steg × 1,6 s ≈ 4,8 s, så siste tekst står til fremdriften er ferdig */
const STEP_MS = 1600;

interface Props {
  phase: LoginPhase;
  /** Varighet på fremdriftslinjen (ms) */
  durationMs: number;
  /** Vises i «Velkommen, …» */
  name: string;
  /** Kalles når utfadingen (leaving) er ferdig */
  onLeft?: () => void;
}

export default function LoginTransition({ phase, durationMs, name, onLeft }: Props) {
  const [step, setStep] = useState(0);

  // Bytter statustekst på timer mens vi kjører (setState kun i timer-callback)
  useEffect(() => {
    if (phase !== 'running') return;
    const timers = STEPS.slice(1).map((_, i) => window.setTimeout(() => setStep(i + 1), (i + 1) * STEP_MS));
    return () => timers.forEach(window.clearTimeout);
  }, [phase]);

  const text = phase === 'done' || phase === 'leaving' ? `Velkommen, ${name}` : STEPS[step];

  return (
    <div className={styles.overlay} data-phase={phase} style={{ ['--lt-duration' as string]: `${durationMs}ms` }}
      onAnimationEnd={(e) => {
        if (phase === 'leaving' && e.target === e.currentTarget) onLeft?.();
      }}
    >
      <div className={styles.center}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/Logo/inSuiteWhiteFontSVG.png" alt="inSuite" className={styles.logo} />
        <div className={styles.horizon} aria-hidden>
          <div className={styles.aura}>
            <span className={styles.auraCore} />
            <span className={styles.auraEdge} />
          </div>
          <div className={styles.track}>
            <span className={styles.line} />
            <span className={styles.light} />
          </div>
        </div>
        <p className={styles.status} role="status" aria-live="polite" key={text}>
          {text}
        </p>
      </div>
    </div>
  );
}
